import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js/dist/hls.min.js";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const MANIFEST_LOAD_TIMEOUT_MS = 12_000;
const FIRST_FRAME_TIMEOUT_MS = 18_000;

function CloseIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function Spinner({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        className="origin-center animate-spin"
        style={{ transformBox: "fill-box" }}
      />
    </svg>
  );
}

function RelatedChannelCard({ related, isActive, onSelect }) {
  const initial = (related.name || "?").slice(0, 2).toUpperCase();

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group relative flex w-[120px] shrink-0 flex-col overflow-hidden rounded-lg border text-left transition duration-200 sm:w-[148px] sm:rounded-xl ${
        isActive
          ? "border-brand-500/60 bg-slate-900/90 shadow-[0_0_0_1px_rgba(42,158,244,0.25)] ring-2 ring-brand-500/30"
          : "border-slate-700/80 bg-slate-900/50 hover:border-slate-600 hover:bg-slate-800/80 hover:shadow-lg"
      }`}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-gradient-to-b from-slate-800 to-slate-950">
        {related.logo ? (
          <img src={related.logo} alt="" className="h-full w-full object-contain p-2 transition duration-200 group-hover:scale-[1.03]" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-800/90">
            <span className="text-sm font-bold tracking-tight text-brand-400/90">{initial}</span>
          </div>
        )}
        {isActive ? (
          <span className="absolute left-2 top-2 rounded-md bg-brand-600/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
            En direct
          </span>
        ) : null}
      </div>
      <div className="border-t border-slate-800/80 p-2.5">
        <p className="line-clamp-2 text-[11px] font-semibold leading-snug text-slate-100 sm:text-xs">{related.name}</p>
      </div>
    </button>
  );
}

const VideoOverlay = ({ setChannel, channel, onClose, channels }) => {
  const videoRef = useRef(null);
  const { language } = useLanguage();
  const isFr = language === "fr";
  const t = useCallback((frText, enText) => (isFr ? frText : enText), [isFr]);

  const iptvUrls = useMemo(
    () => (Array.isArray(channel?.iptv_urls) ? channel.iptv_urls.filter(Boolean) : []),
    [channel?.iptv_urls]
  );
  const youtubeUrls = useMemo(
    () => (Array.isArray(channel?.youtube_urls) ? channel.youtube_urls.filter(Boolean) : []),
    [channel?.youtube_urls]
  );
  const hasIptv = iptvUrls.length > 0;
  const hasYoutube = youtubeUrls.length > 0;
  const navigate = useNavigate();
  const channelList = channels ?? [];

  /**
   * Player state machine:
   *   idle      → no playable source
   *   loading   → attempting current iptv URL
   *   playing   → playback started
   *   failed    → all iptv URLs exhausted (will fall back to YouTube if available, otherwise show error)
   */
  const [playerStatus, setPlayerStatus] = useState(hasIptv ? "loading" : hasYoutube ? "playing" : "idle");
  const [attemptIndex, setAttemptIndex] = useState(0);
  const [forceYoutubeFallback, setForceYoutubeFallback] = useState(false);
  /** Increments to force a full retry from URL #0. */
  const [retryNonce, setRetryNonce] = useState(0);

  const showVideoEl = hasIptv && !forceYoutubeFallback && playerStatus !== "failed";
  const showYouTubeIframe = (forceYoutubeFallback || (!hasIptv && hasYoutube)) && hasYoutube;
  const showErrorPanel = playerStatus === "failed" && !showYouTubeIframe && !showVideoEl;

  /* Reset state when the channel itself changes (or on retry). */
  useEffect(() => {
    setAttemptIndex(0);
    setForceYoutubeFallback(false);
    setPlayerStatus(hasIptv ? "loading" : hasYoutube ? "playing" : "idle");
  }, [channel?.name, hasIptv, hasYoutube, retryNonce]);

  /* Beforeunload guard while a channel is selected. */
  useEffect(() => {
    if (!channel) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [channel]);

  /* Core HLS playback effect with multi-URL fallback. */
  useEffect(() => {
    if (!showVideoEl) return undefined;
    if (!videoRef.current) return undefined;
    /* Defensive: a channel transition can briefly leave attemptIndex
       stale relative to the new iptvUrls. Bail and let the reset
       effect re-fire this effect with a valid index. Exhaustion of
       all URLs is handled inside advanceToNext, not here. */
    if (attemptIndex >= iptvUrls.length) return undefined;

    const videoEl = videoRef.current;
    const streamUrl = iptvUrls[attemptIndex];
    let hlsInstance = null;
    let cancelled = false;

    /* Wall-clock timeouts: protect against hls.js / browser hanging silently
       on a stalled manifest or first segment. */
    let firstFrameTimer = null;
    const armFirstFrameTimer = () => {
      clearTimeout(firstFrameTimer);
      firstFrameTimer = setTimeout(() => {
        if (cancelled) return;
        advanceToNext("first-frame-timeout");
      }, FIRST_FRAME_TIMEOUT_MS);
    };

    const advanceToNext = (reason) => {
      if (cancelled) return;
      cancelled = true;
      clearTimeout(firstFrameTimer);
      try { hlsInstance?.destroy(); } catch (_) { /* noop */ }
      try {
        videoEl.pause();
        videoEl.removeAttribute("src");
        videoEl.load();
      } catch (_) { /* noop */ }

      const nextIndex = attemptIndex + 1;
      if (nextIndex < iptvUrls.length) {
        setAttemptIndex(nextIndex);
      } else if (hasYoutube) {
        /* All IPTV URLs failed but we have a YouTube fallback. */
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(`[VideoOverlay] all IPTV URLs failed (${reason}); switching to YouTube fallback`);
        }
        setForceYoutubeFallback(true);
        setPlayerStatus("playing");
      } else {
        setPlayerStatus("failed");
      }
    };

    const handleNativeError = () => advanceToNext("video-error-event");
    const handlePlaying = () => {
      clearTimeout(firstFrameTimer);
      if (!cancelled) setPlayerStatus("playing");
    };
    const handleStalled = () => {
      /* Re-arm timer on stall; if we never recover, advance. */
      armFirstFrameTimer();
    };

    setPlayerStatus("loading");
    armFirstFrameTimer();

    videoEl.addEventListener("playing", handlePlaying);
    videoEl.addEventListener("error", handleNativeError);
    videoEl.addEventListener("stalled", handleStalled);

    if (Hls.isSupported()) {
      hlsInstance = new Hls({
        manifestLoadingTimeOut: MANIFEST_LOAD_TIMEOUT_MS,
        manifestLoadingMaxRetry: 1,
        levelLoadingTimeOut: MANIFEST_LOAD_TIMEOUT_MS,
        levelLoadingMaxRetry: 1,
        fragLoadingTimeOut: MANIFEST_LOAD_TIMEOUT_MS,
        fragLoadingMaxRetry: 2,
      });

      hlsInstance.on(Hls.Events.ERROR, (_evt, data) => {
        if (cancelled) return;
        if (!data?.fatal) return;
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(`[VideoOverlay] hls fatal ${data.type}/${data.details} on ${streamUrl}`);
        }
        advanceToNext(`hls-${data.type}`);
      });

      try {
        hlsInstance.loadSource(streamUrl);
        hlsInstance.attachMedia(videoEl);
      } catch (err) {
        advanceToNext("hls-throw");
      }
    } else if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      /* Native HLS path (iOS Safari). The 'error' listener attached above
         will trigger advanceToNext on fatal errors. */
      try {
        videoEl.src = streamUrl;
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {
            /* Autoplay may be blocked; that's not a stream failure — wait for user gesture. */
          });
        }
      } catch (_) {
        advanceToNext("native-throw");
      }
    } else {
      /* Browser supports neither: jump straight to fallback chain. */
      advanceToNext("no-hls-support");
    }

    return () => {
      cancelled = true;
      clearTimeout(firstFrameTimer);
      videoEl.removeEventListener("playing", handlePlaying);
      videoEl.removeEventListener("error", handleNativeError);
      videoEl.removeEventListener("stalled", handleStalled);
      try { hlsInstance?.destroy(); } catch (_) { /* noop */ }
      try {
        videoEl.pause();
        videoEl.removeAttribute("src");
        videoEl.load();
      } catch (_) { /* noop */ }
    };
  }, [showVideoEl, attemptIndex, iptvUrls, hasYoutube]);

  const handleClose = () => {
    onClose();
    navigate(-1);
  };

  const switchChannel = (relatedChannel) => {
    /* Do not call onClose() — parent clears selection and breaks in-player switching */
    setChannel(relatedChannel);
  };

  const handleManualRetry = () => {
    setRetryNonce((n) => n + 1);
  };

  if (!channel) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-md">
        <div className="ui-panel max-w-sm px-8 py-10 text-center shadow-panel">
          <p className="text-base font-semibold text-slate-100">{t("Aucune chaîne sélectionnée", "No channel selected")}</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{t("Choisissez une chaîne depuis le guide pour démarrer la lecture.", "Choose a channel from the guide to start playback.")}</p>
          <button type="button" onClick={() => navigate("/")} className="ui-btn-primary mt-8 w-full">
            {t("Retour à l'accueil", "Return home")}
          </button>
        </div>
      </div>
    );
  }

  const totalAttempts = iptvUrls.length;
  const currentAttemptHuman = Math.min(attemptIndex + 1, totalAttempts);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/92 p-0 backdrop-blur-lg sm:p-6">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(42,158,244,0.08),transparent_50%)]"
        aria-hidden
      />
      <div className="relative h-full w-full overflow-hidden rounded-none border-0 bg-slate-900/95 shadow-panel ring-0 sm:h-auto sm:max-w-5xl sm:rounded-3xl sm:border sm:border-slate-700/60 sm:ring-1 sm:ring-white/[0.04]">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/25 to-transparent" aria-hidden />

        <header className="flex items-start justify-between gap-3 border-b border-slate-800/90 bg-slate-950/40 px-3 py-2.5 sm:px-6 sm:py-5">
          <div className="min-w-0 pt-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-400/95 sm:text-[11px] sm:tracking-[0.2em]">{t("Lecture en cours", "Now playing")}</p>
            <h2 className="mt-1 line-clamp-2 text-[15px] font-bold tracking-tight text-white sm:mt-1.5 sm:truncate sm:text-xl">{channel.name}</h2>
            {channel.description ? (
              <p className="mt-1.5 line-clamp-2 max-w-2xl text-xs leading-relaxed text-slate-500 sm:mt-2 sm:text-sm">{channel.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-600/80 bg-slate-800/50 text-slate-400 transition hover:border-slate-500 hover:bg-slate-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/60 active:scale-95 sm:h-11 sm:w-11 sm:rounded-xl"
            onClick={handleClose}
            aria-label={t("Fermer le lecteur", "Close player")}
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </header>

        <div className="relative bg-black">
          <div className="relative aspect-video w-full ring-1 ring-inset ring-black/40">
            {showVideoEl ? (
              <video
                ref={videoRef}
                controls
                autoPlay
                className="h-full w-full object-contain"
                playsInline
              />
            ) : null}

            {showYouTubeIframe ? (
              <iframe
                key={`${channel.name}-yt`}
                className="h-full w-full"
                src={convertToYouTubeEmbed(youtubeUrls[0])}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                title={channel.name}
              />
            ) : null}

            {showErrorPanel ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-950/95 p-6 text-center">
                <div className="max-w-sm">
                  <p className="text-base font-semibold text-slate-100">
                    {t("Flux indisponible", "Stream unavailable")}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    {totalAttempts > 1
                      ? t(
                          `Aucune des ${totalAttempts} sources n'a répondu. Le flux peut être hors ligne ou bloqué dans votre région.`,
                          `None of the ${totalAttempts} sources responded. The stream may be offline or blocked in your region.`
                        )
                      : t(
                          "La source de cette chaîne ne répond pas. Elle peut être hors ligne ou bloquée dans votre région.",
                          "This channel's source isn't responding. It may be offline or blocked in your region."
                        )}
                  </p>
                  <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
                    <button type="button" onClick={handleManualRetry} className="ui-btn-primary">
                      {t("Réessayer", "Try again")}
                    </button>
                    <button type="button" onClick={handleClose} className="ui-btn-ghost">
                      {t("Fermer", "Close")}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {!hasIptv && !hasYoutube ? (
              <div className="absolute inset-0 flex items-center justify-center p-10 text-center">
                <div>
                  <p className="font-medium text-slate-300">{t("Aucun flux disponible", "No stream available")}</p>
                  <p className="mt-2 text-sm text-slate-500">{t("Cette chaîne n'a pas d'URL HLS ou YouTube dans l'index.", "This channel has no HLS or YouTube URL in the index.")}</p>
                </div>
              </div>
            ) : null}

            {showVideoEl && playerStatus === "loading" ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/55">
                <div className="flex flex-col items-center gap-3 text-slate-200">
                  <Spinner className="h-8 w-8 text-brand-400" />
                  <p className="text-xs font-medium tracking-wide">
                    {totalAttempts > 1
                      ? t(
                          `Connexion à la source ${currentAttemptHuman}/${totalAttempts}…`,
                          `Connecting to source ${currentAttemptHuman}/${totalAttempts}…`
                        )
                      : t("Connexion au flux…", "Connecting to stream…")}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {channelList.length > 0 ? (
          <footer className="border-t border-slate-800/90 bg-gradient-to-b from-slate-950/90 to-slate-950 px-2.5 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] sm:px-6 sm:py-5 sm:pb-5">
            <div className="mb-2.5 flex items-end justify-between gap-3 sm:mb-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-400/90 sm:text-[11px] sm:tracking-[0.2em]">{t("Plus dans cette liste", "More in this list")}</p>
                <p className="mt-0.5 text-[11px] text-slate-500 sm:mt-1 sm:text-xs">{t("Touchez une chaîne pour changer sans quitter le lecteur", "Tap a channel to switch without leaving the player")}</p>
              </div>
              <span className="hidden shrink-0 text-xs tabular-nums text-slate-600 sm:inline">{channelList.length} {t("chaînes", "channels")}</span>
            </div>
            <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1 pt-0.5 [scrollbar-width:thin]">
              {channelList.map((relatedChannel) => (
                <RelatedChannelCard
                  key={relatedChannel.name}
                  related={relatedChannel}
                  isActive={relatedChannel.name === channel.name}
                  onSelect={() => switchChannel(relatedChannel)}
                />
              ))}
            </div>
          </footer>
        ) : null}
      </div>
    </div>
  );
};

const convertToYouTubeEmbed = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);

    if (parsed.pathname.startsWith("/embed/")) {
      parsed.searchParams.set("autoplay", "1");
      parsed.searchParams.set("playsinline", "1");
      parsed.searchParams.set("rel", "0");
      return parsed.toString();
    }

    if (parsed.hostname.includes("youtube.com")) {
      const videoId = parsed.searchParams.get("v");
      if (videoId) return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
    }

    if (parsed.hostname.includes("youtu.be")) {
      const videoId = parsed.pathname.replace("/", "");
      if (videoId) return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
    }
  } catch {
    /* Ignore parse errors and return original URL. */
  }
  return url;
};

export default VideoOverlay;
